import { useEffect, useState, useCallback, useMemo } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { RefreshCw, BarChart3 } from 'lucide-react'
import './AdBuilder.css'

// Lightweight inline SVG bar-chart for the 24h hourly sparkline.
// Avoids pulling in recharts just for one tiny visualization.
function Sparkline({ buckets, valueKey, color, label }) {
  const max = Math.max(1, ...buckets.map(b => b[valueKey] || 0))
  const W = 360
  const H = 56
  const padX = 2
  const innerW = W - padX * 2
  const bw = innerW / Math.max(1, buckets.length)
  return (
    <div className="ab-spark">
      <div className="ab-spark-label">
        <span style={{ color }}>■</span> {label} (last 24h, max bar = {max})
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke="#333" strokeWidth="0.5" />
        {buckets.map((b, i) => {
          const v = b[valueKey] || 0
          const h = (v / max) * (H - 4)
          return (
            <rect
              key={b.hour + '-' + i}
              x={padX + i * bw}
              y={H - h - 1}
              width={Math.max(1, bw - 1)}
              height={h}
              fill={color}
              opacity={v ? 0.85 : 0.2}
            >
              <title>{`${b.hour}:00 — ${v} ${label.toLowerCase()}`}</title>
            </rect>
          )
        })}
      </svg>
    </div>
  )
}

function fmtRelative(ts) {
  if (!ts) return '—'
  const ms = Date.now() - new Date(ts.replace(' ', 'T') + 'Z').getTime()
  if (isNaN(ms)) return ts
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
  return `${Math.floor(ms / 86_400_000)}d ago`
}

function fmtAdSpeedStats(data) {
  // AdSpeed AS.Zone.getStats returns various shapes; surface what we can
  // find without locking to one schema. Most responses are a `Stats`
  // wrapper with date-keyed sub-elements totaling out at the top level.
  if (!data) return { total: '—', breakdown: [] }
  const stats = data.Zone?.Stats ?? data.Stats ?? data
  const total = stats?.Total ?? stats?.total ?? stats?.Value ?? null
  return {
    total: total != null ? total : '—',
    raw: stats,
  }
}

// Build a complete hour-by-hour series for the last 24h so missing
// hours show as zero bars instead of being skipped.
function padHourly(rows) {
  const map = new Map((rows || []).map(r => [r.hour, r]))
  const out = []
  const now = new Date()
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 3600_000)
    const key = `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 13)}`
    const row = map.get(key) || { hour: key, loads: 0, transitions: 0 }
    out.push(row)
  }
  return out
}

export default function AdBuilderStatsCard({ slug, zoneId }) {
  const { api } = useAuth()
  const [pageStats, setPageStats] = useState(null)
  const [zoneStats, setZoneStats] = useState(null)
  const [period, setPeriod] = useState('week')
  // Tracks whether the dropdown selection differs from the period the
  // currently-displayed zoneStats was fetched for. Surfaces a hint that
  // a Refresh is needed before the AdSpeed number updates.
  const [appliedPeriod, setAppliedPeriod] = useState('week')
  const [pageErr, setPageErr] = useState('')
  const [zoneErr, setZoneErr] = useState('')
  const [loading, setLoading] = useState(false)

  // NOTE: `period` is deliberately NOT in the dependency array. Changing
  // the dropdown does NOT trigger a fetch — admin must click Refresh to
  // commit. This keeps idle dropdown-browsing from chewing AdSpeed call
  // quota; each unique (metric, period) is its own cache key upstream.
  const loadAll = useCallback(async (overridePeriod) => {
    if (!slug) return
    const usePeriod = overridePeriod || period
    setLoading(true)
    setPageErr('')
    setZoneErr('')
    const tasks = []
    tasks.push(
      api(`/ad-builder/${slug}/stats`)
        .then(d => setPageStats(d))
        .catch(e => setPageErr(e.message))
    )
    if (zoneId) {
      tasks.push(
        api(`/sponsors/zones/${zoneId}/stats?metric=impression&period=${usePeriod}`)
          .then(d => {
            setZoneStats(d?.ok === false ? null : d)
            setAppliedPeriod(usePeriod)
          })
          .catch(e => setZoneErr(e.message))
      )
    }
    await Promise.allSettled(tasks)
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, slug, zoneId])

  // Initial load only — `period` intentionally omitted to avoid auto-fetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll() }, [slug, zoneId])

  const hourly = useMemo(() => padHourly(pageStats?.hourly_24h), [pageStats])
  const zone = useMemo(() => fmtAdSpeedStats(zoneStats?.data), [zoneStats])

  if (!slug) return null

  const p = pageStats || { loads: {}, transitions: {} }
  const compareRow = pageStats && zoneStats?.ok && zone.total !== '—'
    ? `This page fired ${(p.transitions?.last_7d ?? 0).toLocaleString()} transitions in the last 7d; AdSpeed shows ${
        (Number(zone.total) || zone.total).toLocaleString?.() ?? zone.total
      } zone impressions same period.`
    : null

  return (
    <div className="ab-stats-card">
      <div className="ab-stats-header">
        <h3>
          <BarChart3 size={14} style={{ marginRight: 6 }} />
          Analytics
        </h3>
        <div className="ab-stats-controls">
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="ab-stats-period"
            title="AdSpeed reporting period (Refresh to apply)"
          >
            <option value="today">today</option>
            <option value="week">7 days</option>
            <option value="month">30 days</option>
            <option value="year">year</option>
          </select>
          <button
            className="sponsors-refresh-btn"
            onClick={() => loadAll()}
            disabled={loading}
            title={period !== appliedPeriod
              ? `Refresh to apply ${period}`
              : 'Refresh stats'}
          >
            <RefreshCw size={13} />
            {period !== appliedPeriod ? ' Apply' : ' Refresh'}
          </button>
        </div>
      </div>

      <div className="ab-stats-grid">
        {/* AdSpeed zone-scoped */}
        <div className="ab-stats-block">
          <div className="ab-stats-block-title">AdSpeed zone billing</div>
          {zoneErr ? (
            <div className="ab-stats-error">Error: {zoneErr}</div>
          ) : zoneId == null ? (
            <div className="ab-stats-muted">No zone configured</div>
          ) : zoneStats === null && !loading ? (
            <div className="ab-stats-muted">No data (zone may have no impressions yet)</div>
          ) : (
            <>
              <div className="ab-stats-bignum">
                {(Number(zone.total) || zone.total).toLocaleString?.() ?? zone.total}
              </div>
              <div className="ab-stats-sublabel">
                impressions · {appliedPeriod} · zone {zoneId}
              </div>
              <div className="ab-stats-foot">
                Includes every embed of this zone, not just this page.
              </div>
            </>
          )}
        </div>

        {/* Page-scoped from our beacons */}
        <div className="ab-stats-block">
          <div className="ab-stats-block-title">This ad page</div>
          {pageErr ? (
            <div className="ab-stats-error">Error: {pageErr}</div>
          ) : (
            <>
              <div className="ab-stats-row">
                <span className="ab-stats-row-k">Page loads</span>
                <span className="ab-stats-row-v">
                  {(p.loads?.lifetime ?? 0).toLocaleString()}
                  <span className="ab-stats-row-sub">
                    · 7d {(p.loads?.last_7d ?? 0).toLocaleString()}
                    · 24h {(p.loads?.last_24h ?? 0).toLocaleString()}
                  </span>
                </span>
              </div>
              <div className="ab-stats-row">
                <span className="ab-stats-row-k">Ad transitions</span>
                <span className="ab-stats-row-v">
                  {(p.transitions?.lifetime ?? 0).toLocaleString()}
                  <span className="ab-stats-row-sub">
                    · 7d {(p.transitions?.last_7d ?? 0).toLocaleString()}
                    · 24h {(p.transitions?.last_24h ?? 0).toLocaleString()}
                  </span>
                </span>
              </div>
              <div className="ab-stats-row">
                <span className="ab-stats-row-k">Last transition</span>
                <span className="ab-stats-row-v">{fmtRelative(pageStats?.last_seen_transition)}</span>
              </div>
              <div className="ab-stats-row">
                <span className="ab-stats-row-k">Last page load</span>
                <span className="ab-stats-row-v">{fmtRelative(pageStats?.last_seen_load)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {pageStats && (
        <div className="ab-stats-sparks">
          <Sparkline buckets={hourly} valueKey="loads"       color="#93c5fd" label="Loads" />
          <Sparkline buckets={hourly} valueKey="transitions" color="#4ade80" label="Transitions" />
        </div>
      )}

      {pageStats?.ua_breakdown_7d?.length > 0 && (
        <div className="ab-stats-ua">
          <span className="ab-stats-row-k">Loads by client (7d)</span>
          {pageStats.ua_breakdown_7d.map(u => (
            <span key={u.ua_class} className="ab-stats-ua-chip">
              {u.ua_class}: {u.count}
            </span>
          ))}
        </div>
      )}

      {compareRow && (
        <div className="ab-stats-compare">
          <span>≈ Comparison:</span> {compareRow}
        </div>
      )}
    </div>
  )
}
